import * as Yup from 'yup';

// Add Vendor Validation Schema
export const addVendorValidationSchema = Yup.object().shape({
    companyName: Yup.string()
        .required('Company name is required')
        .min(2, 'Company name must be at least 2 characters'),
    contactPerson: Yup.string()
        .required('Contact person is required')
        .min(2, 'Contact person name must be at least 2 characters'),
    category: Yup.string()
        .required('Category is required'),
    phone: Yup.string()
        .required('Phone number is required')
        .matches(/^[0-9+\s-]{10,15}$/, 'Invalid phone number'),
    alternatePhone: Yup.string()
        .matches(/^[0-9+\s-]{10,15}$/, 'Invalid phone number')
        .nullable(),
    email: Yup.string()
        .email('Invalid email address')
        .nullable(),
    website: Yup.string()
        .url('Invalid URL')
        .nullable(),
    amount: Yup.number()
        .typeError('Avg bill amount must be a number')
        .required('Avg bill amount is required')
        .min(0, 'Avg bill amount must be positive'),
    panNumber: Yup.string()
        .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, 'Invalid PAN format (e.g., ABCDE1234F)')
        .nullable(),
    gstNumber: Yup.string()
        .matches(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, 'Invalid GST format')
        .nullable(),
    tanNumber: Yup.string()
        .matches(/^[A-Z]{4}[0-9]{5}[A-Z]{1}$/, 'Invalid TAN format (e.g., ABCD12345E)')
        .nullable(),
    address: Yup.string().nullable(),
    countryId: Yup.string().required('Country is required'),
    stateId: Yup.string().required('State is required'),
    cityId: Yup.string().required('City is required'),
    pincode: Yup.string()
        .matches(/^[0-9]{6}$/, 'Pincode must be 6 digits')
        .nullable(),
    contractStartDate: Yup.date().nullable(),
    contractEndDate: Yup.date()
        .min(Yup.ref('contractStartDate'), 'End date must be after start date')
        .nullable(),
    contractAmount: Yup.number()
        .min(0, 'Amount must be positive')
        .nullable(),
    paymentTerms: Yup.string(),
    contractStatus: Yup.string(),
    status: Yup.string(),
    notes: Yup.string().nullable(),
    msmeStatus: Yup.string().required('MSME Status is required'),
    rcmApplicable: Yup.string(),
    tdsApplicable: Yup.string(),
});

// Edit Vendor Validation Schema (can be same or different)
export const editVendorValidationSchema = addVendorValidationSchema;

// Add Vendor Bill Validation Schema
export const addVendorBillValidationSchema = Yup.object().shape({
    vendorId: Yup.string().required('Vendor is required'),
    billType: Yup.string().required('Bill type is required'),
    billNumber: Yup.string().required('Bill number is required'),
    billDate: Yup.date().required('Bill date is required'),
    description: Yup.string().required('Description is required'),
    dueDate: Yup.date().required('Due date is required'),
    tariffDetails: Yup.array().of(
        Yup.object().shape({
            headId: Yup.string().required('Head name is required'),
            qty: Yup.number().nullable().transform((value, original) => original === '' ? null : value)
                .when('itemType', {
                    is: 'unit',
                    then: (schema) => schema.positive('Qty must be positive').required('Qty is required'),
                    otherwise: (schema) => schema.nullable(),
                }),
            unit: Yup.string().nullable()
                .when('itemType', {
                    is: 'unit',
                    then: (schema) => schema.required('Unit is required'),
                    otherwise: (schema) => schema.nullable(),
                }),
            amount: Yup.number().nullable().transform((value, original) => original === '' ? null : value)
                .positive('Amount must be positive').required('Amount is required'),
            cgstRate: Yup.number().nullable().transform((value, original) => original === '' ? null : value)
                .min(0, 'Cannot be negative'),
            sgstRate: Yup.number().nullable().transform((value, original) => original === '' ? null : value)
                .min(0, 'Cannot be negative'),
            igstRate: Yup.number().nullable().transform((value, original) => original === '' ? null : value)
                .min(0, 'Cannot be negative'),
            itemType: Yup.string().nullable(),
            tdsRate: Yup.number().nullable().transform((value, original) => original === '' ? null : value)
                .min(0, 'Cannot be negative'),
            tdsSection: Yup.string().nullable(),
        })
    ).min(1, 'At least one tariff detail is required'),
});

// Add Vendor Payment Validation Schema
export const addVendorPaymentValidationSchema = Yup.object().shape({
    vendorId: Yup.string().required('Vendor is required'),
    paymentType: Yup.string().required('Payment type is required'),
    paymentDate: Yup.date().required('Payment date is required'),
    amount: Yup.number()
        .required('Payment amount is required')
        .positive('Amount must be positive'),
    paymentMode: Yup.string().required('Payment mode is required'),
    // Cash is forced to Cash in Hand server-side; every other mode needs an account
    // chosen, otherwise the payment has no debit side and can never post.
    paidThrough: Yup.string().when('paymentMode', {
        is: (mode) => mode && mode !== 'cash',
        then: (schema) => schema.required('Select the bank account this is paid from'),
        otherwise: (schema) => schema.nullable(),
    }),
    referenceNo: Yup.string().nullable(),
    notes: Yup.string().nullable(),
});

// Add Vendor Payment Initial Values
export const addVendorPaymentInitialValues = {
    vendorId: '',
    paymentType: '',
    paymentDate: '',
    amount: '',
    paymentMode: '',
    referenceNo: '',
    notes: '',
    document: null,
    billPayments: [],
};

// Add Vendor Initial Values
export const addVendorInitialValues = {
    companyName: '',
    contactPerson: '',
    category: '',
    phone: '',
    alternatePhone: '',
    email: '',
    website: '',
    amount: '',
    gstNumber: '',
    panNumber: '',
    tanNumber: '',
    address: '',
    countryId: '',
    stateId: '',
    cityId: '',
    pincode: '',
    contractStartDate: '',
    contractEndDate: '',
    contractAmount: '',
    paymentTerms: 'monthly',
    contractStatus: 'Active',
    status: 'Active',
    notes: '',
    msmeStatus: '',
    rcmApplicable: 'N',
    tdsApplicable: 'N',
    role_id: '',
};
